/**
 * @voltxampere/web — Engine worker client (Sprint 14.0.1, D7 closure)
 *
 * Promise-based wrapper around the Sprint 9 `EngineWorkerClient`.
 * Singleton: the first `solveDc` call spawns a Vite-bundled Web
 * Worker, runs `init` once, then issues `runAnalysis({ kind: "DC" })`
 * for every subsequent call after `updateCircuit`.
 *
 *   - 10-second timeout per `solveDc` call. The timer fires its
 *     own rejection so the inspector can surface a clear error
 *     instead of hanging indefinitely on a pathological circuit.
 *   - Worker errors (`EngineWorkerClientError`, `worker.onerror`)
 *     bubble out of the Promise so `runDc` can write `dcError`.
 *   - The shell injects an alternate runner via `setEngineClient`
 *     in tests — no real Worker is spawned in the vitest suite.
 *
 * Sprint 15 will extend this surface with `runTransient(circuit,
 * onFrame)` against the same client; the singleton boundary
 * stays so wiring does not change.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { EngineWorkerClient } from "@voltxampere/engine";
import type { AnalysisResult, WorkerLike } from "@voltxampere/engine";
import type { Circuit } from "@voltxampere/schema";

import { DEFAULT_TEMPERATURE_K, temperatureK } from "./sim-prefs.js";

export const DEFAULT_DC_TIMEOUT_MS = 10_000;
export const DEFAULT_TRANSIENT_TIMEOUT_MS = 30_000;
export const DEFAULT_AC_TIMEOUT_MS = 30_000;

/**
 * Sprint 66.1 hotfix (ADR-0101, KOL-A-ENGINE-1) — strip every
 * underscore-prefixed key from `wires`, `components`, and `nets`
 * before posting the circuit envelope to the engine worker. The
 * canvas renderer (Sprint 52 ADR-0085) mutates each wire with
 * `_catenarySamples` to cache hit-test samples — that cache is
 * renderer-only and was never meant to leave the main thread, but
 * the mutation lived on the same object passed downstream and the
 * engine's `EngineRequestSchema` (Zod `strict()`) rejects every
 * such envelope. Every transient analysis since v3.4.0-beta.2 has
 * been failing silently because of this leak.
 *
 * The sanitizer is non-mutating: it shallow-clones every wire /
 * component / net and copies only the keys that DO NOT start with
 * `_`. Engine zero-diff invariant is preserved (the engine package
 * is untouched; the fix lives entirely on the apps/web caller).
 */
function stripRendererAnnotations<T extends object>(o: T): T {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(o)) {
    if (key.startsWith("_")) continue;
    out[key] = (o as Record<string, unknown>)[key];
  }
  return out as T;
}

/**
 * Sprint 80.Z L-AYARLAR-5 — Temperature ENGINE-BOUND injection.
 *
 * The AYARLAR > Simülasyon > Sıcaklık input drives the
 * `temperatureK` signal (`sim-prefs.ts`, Sprint 24 A3). Until this
 * lane the signal value never reached the engine — every
 * `runAnalysis` round-tripped the fixture's hardcoded
 * `simulationSettings.dc.temperature` (27 °C for every preset).
 *
 * Unit contract:
 *   - `temperatureK` signal stores **Kelvin** (SI, engine-side stamp
 *     context convention)
 *   - `simulationSettings.dc.temperature` schema field is
 *     **Celsius** (SPICE `.OPTIONS TEMP=27` convention; DCSettings-
 *     Schema default = 27)
 *   - This sanitizer converts Kelvin → Celsius at the wire boundary
 *     so the schema's invariant is preserved
 *   - The engine solver re-converts Celsius → Kelvin internally
 *     before computing `Vt = kT/q`
 *
 * Injection rules:
 *   - When the user signal matches the default 300.15 K (27 °C),
 *     pass the fixture's existing setting through unchanged so
 *     sentinel circuits stay bit-identical.
 *   - When the user has chosen a non-default temperature, OVERWRITE
 *     `circuit.simulationSettings.dc.temperature` with the Celsius
 *     equivalent of the signal.
 */
function applyUserTemperature(circuit: Circuit): Circuit {
  const userTempK = temperatureK.value;
  if (userTempK === DEFAULT_TEMPERATURE_K) return circuit;
  const userTempC = userTempK - 273.15;
  return {
    ...circuit,
    simulationSettings: {
      ...circuit.simulationSettings,
      dc: {
        ...circuit.simulationSettings.dc,
        temperature: userTempC,
      },
    },
  };
}

export function sanitizeCircuitForEngine(circuit: Circuit): Circuit {
  const withTemp = applyUserTemperature(circuit);
  return {
    ...withTemp,
    wires: withTemp.wires.map(stripRendererAnnotations),
    components: withTemp.components.map(stripRendererAnnotations),
    nets: withTemp.nets.map(stripRendererAnnotations),
  };
}

interface ClientState {
  readonly client: EngineWorkerClient;
  initialised: boolean;
  initPromise: Promise<void> | null;
}

let state: ClientState | null = null;

/**
 * Tests can inject a fake transport (anything that satisfies
 * `WorkerLike`) so they exercise the wrapper without spawning a
 * real Worker. Resets every time it is called — the next
 * `solveDc` will re-init from scratch.
 */
export function setEngineWorker(transport: WorkerLike | null): void {
  state = transport
    ? { client: new EngineWorkerClient(transport), initialised: false, initPromise: null }
    : null;
}

/**
 * Lazily spin the singleton. The Vite `?worker` import is
 * deferred behind dynamic `import()` so the module graph stays
 * test-friendly (Vitest fails to resolve `?worker` URLs).
 */
async function ensureClient(): Promise<ClientState> {
  if (state) return state;
  const { default: EngineWorker } = await import("../workers/engine-worker.ts?worker");
  const worker = new EngineWorker();
  state = { client: new EngineWorkerClient(worker), initialised: false, initPromise: null };
  return state;
}

/**
 * Issue a DC analysis. The first call inits the worker with the
 * supplied circuit; subsequent calls send `updateCircuit` so
 * the engine swaps fixtures without restarting.
 */
export async function solveDc(
  circuit: Circuit,
  opts: { timeoutMs?: number } = {},
): Promise<AnalysisResult> {
  const s = await ensureClient();
  const timeoutMs = opts.timeoutMs ?? DEFAULT_DC_TIMEOUT_MS;
  const clean = sanitizeCircuitForEngine(circuit);

  if (!s.initialised) {
    s.initPromise ??= s.client.init(clean);
    try {
      await s.initPromise;
      s.initialised = true;
    } catch (err) {
      s.initPromise = null;
      throw err;
    }
  } else {
    await s.client.updateCircuit(clean);
  }

  return await withTimeout(s.client.runAnalysis({ kind: "DC" }), timeoutMs);
}

/**
 * Issue a transient analysis (Sprint 15). Same lazy-init lifecycle
 * as `solveDc`. Per-call `tstop` overrides the circuit's
 * simulationSettings; the wire format carries the full per-step
 * time series via `meta.timeSeries` (ADR-0027 / D9).
 */
export async function solveTransient(
  circuit: Circuit,
  opts: { timeoutMs?: number; tstop?: number; signal?: AbortSignal } = {},
): Promise<AnalysisResult> {
  const s = await ensureClient();
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TRANSIENT_TIMEOUT_MS;
  const clean = sanitizeCircuitForEngine(circuit);

  if (!s.initialised) {
    s.initPromise ??= s.client.init(clean);
    try {
      await s.initPromise;
      s.initialised = true;
    } catch (err) {
      s.initPromise = null;
      throw err;
    }
  } else {
    await s.client.updateCircuit(clean);
  }

  const analysis =
    opts.tstop !== undefined
      ? { kind: "TRANSIENT" as const, tstop: opts.tstop }
      : { kind: "TRANSIENT" as const };
  return await withTimeout(s.client.runAnalysis(analysis), timeoutMs);
}

/**
 * Sprint 20 — issue an AC sweep. Same lazy-init lifecycle as the
 * DC + transient helpers. Per-call sweep override is forwarded as
 * `analysis.acSweep`; absent fields fall back to the circuit's
 * `simulationSettings.ac` defaults at the engine boundary.
 */
export async function solveAc(
  circuit: Circuit,
  opts: {
    timeoutMs?: number;
    sweep?: {
      fStart?: number;
      fStop?: number;
      points?: number;
      sweep?: "dec" | "oct" | "lin";
    };
    signal?: AbortSignal;
  } = {},
): Promise<AnalysisResult> {
  const s = await ensureClient();
  const timeoutMs = opts.timeoutMs ?? DEFAULT_AC_TIMEOUT_MS;
  const clean = sanitizeCircuitForEngine(circuit);

  if (!s.initialised) {
    s.initPromise ??= s.client.init(clean);
    try {
      await s.initPromise;
      s.initialised = true;
    } catch (err) {
      s.initPromise = null;
      throw err;
    }
  } else {
    await s.client.updateCircuit(clean);
  }

  const analysis =
    opts.sweep !== undefined
      ? { kind: "AC" as const, acSweep: opts.sweep }
      : { kind: "AC" as const };
  return await withTimeout(s.client.runAnalysis(analysis), timeoutMs);
}

/**
 * Drop the in-memory client + worker. Tests use this between
 * runs; production callers normally never call it (the worker
 * lives for the lifetime of the page).
 */
export async function disposeEngineClient(): Promise<void> {
  if (!state) return;
  try {
    await state.client.destroy();
  } catch {
    // best-effort — the worker may already be torn down
  }
  state = null;
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`DC solve timeout after ${String(ms)}ms`));
    }, ms);
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err instanceof Error ? err : new Error(String(err)));
      },
    );
  });
}
